import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  RefreshCw,
  Trophy,
  Check,
  X,
  Zap,
  Target,
  Clock,
  Loader,
  Volume2,
} from "lucide-react";
import "../styles/wordRelationsGame.css";
import LoadingOverlay from "../components/LoadingOverlay";
import { API_BASE } from "../config";
import gameConfig from "../data/gameConfig.json";
import apiFetch from "../apiFetch";
import { reportHomeworkCompletion } from "../utils/homeworkCompletion";
import { reportDailyTaskCompletion } from "../utils/dailyTaskCompletion";
import { navigateReturn } from "../utils/navigation";
import { YEAR_OPTIONS, DISPLAY_YEAR_OPTIONS, mapToNearestTier, filterWordsByTier, tierLabel } from "../data/vocabYearGroups";

const CFG = gameConfig.synonyms;

const KEYS = ["A", "B", "C", "D"];

const SynonymsGame = () => {
  const navigate = useNavigate();
  const returnTo = useRef(sessionStorage.getItem("returnTo") || "/games/vocabulary");
  const goBack = () => { sessionStorage.removeItem("returnTo"); navigate(returnTo.current); };
  const firstOptionRef = useRef(null);

  const [gameState, setGameState] = useState("loading"); // loading | playing | feedback | finished
  const [vocabulary, setVocabulary] = useState([]);
  const [userElo, setUserElo] = useState(null);
  const [selectedTier, setSelectedTier] = useState(() =>
    mapToNearestTier(localStorage.getItem("difficulty_tier"))
  );
  const [question, setQuestion] = useState(null); // { word, definition, tier, options, correctAnswer }
  const [selected, setSelected] = useState(null);
  const [correct, setCorrect] = useState(0);
  const [wrong, setWrong] = useState(0);
  const [streak, setStreak] = useState(0);
  const [answered, setAnswered] = useState(0);
  const [startTime] = useState(Date.now());
  const [eloLog, setEloLog] = useState([]);
  const [coinsEarned, setCoinsEarned] = useState(0);
  const [speaking, setSpeaking] = useState(false);
  const [hwAssignmentId, setHwAssignmentId] = useState(null);
  const shownThisSession = useRef(new Set());

  /* ── Cache homework assignmentId on mount (so Play Again sessions can still report) ── */
  useEffect(() => {
    const raw = sessionStorage.getItem("hw_params");
    if (raw) {
      try { const p = JSON.parse(raw); if (p?.assignmentId) setHwAssignmentId(p.assignmentId); } catch {}
    }
  }, []);

  /* ── Cancel speech on unmount ── */
  useEffect(() => () => window.speechSynthesis.cancel(), []);

  const speak = useCallback((question) => {
    if (window.speechSynthesis.speaking) {
      window.speechSynthesis.cancel();
      setSpeaking(false);
      return;
    }
    const text =
      `Which word is a synonym of ${question.word}? It means: ${question.definition}. ` +
      question.options.map((opt, i) => `${KEYS[i]}: ${opt}`).join(". ");
    const utt = new SpeechSynthesisUtterance(text);
    utt.rate = 0.88;
    utt.onstart = () => setSpeaking(true);
    utt.onend = () => setSpeaking(false);
    utt.onerror = () => setSpeaking(false);
    window.speechSynthesis.speak(utt);
  }, []);

  /* ── Load vocab + ELO on mount ── */
  useEffect(() => {
    (async () => {
      try {
        const userId = parseInt(localStorage.getItem("userId"), 10);
        const [eloRes, vocabRes] = await Promise.all([
          apiFetch(`/api/synonyms/current-elo?userId=${userId}`),
          apiFetch(`/api/vocabulary`),
        ]);
        const { elo } = await eloRes.json();
        if (!vocabRes.ok) throw new Error(`Vocabulary fetch failed: ${vocabRes.status}`);
        const { words = [] } = await vocabRes.json();
        setUserElo(elo ?? CFG.defaultElo);
        setVocabulary(words);
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  /* ── Generate a question only when the game is waiting for one ── */
  useEffect(() => {
    if (vocabulary.length > 0 && userElo !== null && gameState === "loading") {
      generateQuestion(vocabulary, userElo);
    }
  }, [vocabulary, userElo, gameState]);

  /* ── Focus the first option whenever a new question appears ── */
  useEffect(() => {
    if (gameState === "playing") {
      firstOptionRef.current?.focus();
    }
  }, [question, gameState]);

  /* ── Pick a question from the available pool ── */
  const generateQuestion = useCallback((vocab, elo) => {
    const tierWords = filterWordsByTier(vocab, selectedTier);
    let eligible = tierWords.filter(
      (w) => w.synonyms && w.synonyms.length > 0 && !shownThisSession.current.has(w.word),
    );

    // All words shown this session — reset cycle and start again
    if (eligible.length === 0) {
      shownThisSession.current.clear();
      eligible = tierWords.filter((w) => w.synonyms && w.synonyms.length > 0);
    }

    if (eligible.length === 0) {
      setGameState("finished");
      return;
    }

    // Pick a random unshown word and mark it shown
    const word = eligible[Math.floor(Math.random() * eligible.length)];
    shownThisSession.current.add(word.word);
    const correct =
      word.synonyms[Math.floor(Math.random() * word.synonyms.length)];

    // 3 distractor words from same tier pool (not synonyms/antonyms of this word)
    const excluded = new Set([
      word.word.toLowerCase(),
      ...(word.synonyms || []).map((s) => s.toLowerCase()),
      ...(word.antonyms || []).map((a) => a.toLowerCase()),
    ]);
    const distractors = tierWords
      .filter((w) => !excluded.has(w.word.toLowerCase()))
      .sort(() => Math.random() - 0.5)
      .slice(0, 3)
      .map((w) => w.word);

    const options = [correct, ...distractors].sort(() => Math.random() - 0.5);

    window.speechSynthesis.cancel();
    setSpeaking(false);
    setQuestion({
      word: word.word,
      definition: word.definition,
      tier: word.tier,
      options,
      correctAnswer: correct,
    });
    setSelected(null);
    setGameState("playing");
  }, [selectedTier]);

  /* ── Handle option selection ── */
  const handleSelect = (option) => {
    if (gameState !== "playing") return;
    setSelected(option);
    setGameState("feedback");
    setAnswered((n) => n + 1);

    const isCorrect = option === question.correctAnswer;
    const eloChange = isCorrect ? CFG.eloCorrect : -CFG.eloPenalty;
    const newElo = Math.max(0, userElo + eloChange);

    setUserElo(newElo);
    setEloLog((prev) => [...prev, { word: question.word, change: eloChange }]);

    if (isCorrect) {
      setCorrect((n) => n + 1);
      setStreak((n) => n + 1);
    } else {
      setWrong((n) => n + 1);
      setStreak(0);
    }

    setTimeout(() => generateQuestion(vocabulary, newElo), 1600);
  };

  const finishSession = async () => {
    // Nothing was attempted — don't report completion, just exit
    if (answered === 0) {
      if (returnTo.current === "/daily-tasks") {
        navigate(returnTo.current);
      } else {
        setGameState("finished");
      }
      return;
    }

    // Replenish hw_params if a previous Play-Again session already consumed it
    if (hwAssignmentId && !sessionStorage.getItem("hw_params")) {
      sessionStorage.setItem("hw_params", JSON.stringify({ assignmentId: hwAssignmentId }));
    }

    const userId = parseInt(localStorage.getItem("userId"), 10);

    // Fire-and-forget: persist words played this session for cross-session unplayed-first ordering
    const sessionAnswers = eloLog.map((e) => ({ wordId: e.word, correct: e.change > 0 }));
    if (sessionAnswers.length > 0) {
      apiFetch("/api/vocab-test/save-session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, testType: "synonyms", wordCount: sessionAnswers.length, score: correct, maxScore: answered, answers: sessionAnswers, tier: selectedTier }),
      }).catch(() => {});
    }
    const coins = Math.floor(
      correct * Math.max(CFG.coinsEloMin, userElo / CFG.coinsEloScale),
    );
    setCoinsEarned(coins);
    if (returnTo.current === "/daily-tasks") {
      sessionStorage.removeItem("returnTo");
      navigate(returnTo.current);
    } else {
      setGameState("finished");
    }

    try {
      await apiFetch(`/api/synonyms/save-elo`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, finalElo: userElo, tier: selectedTier }),
      });
      await reportHomeworkCompletion(null, "game", correct);
      reportDailyTaskCompletion(correct);
      if (coins > 0) {
        await apiFetch(`/api/resources/update-coins`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userId, delta: coins }),
        });
        await apiFetch(`/api/resources/xp/add`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userId, delta: coins * CFG.xpMultiplier }),
        });
      }
    } catch (e) {
      console.error(e);
    }

  };

  const formatTime = (ms) => {
    const s = Math.floor(ms / 1000);
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
  };

  /* ── Loading ── */
  if (gameState === "loading" || userElo === null) return <LoadingOverlay message="Loading Synonyms Game…" />;

  /* ── Results ── */
  if (gameState === "finished") {
    const accuracy = answered > 0 ? Math.round((correct / answered) * 100) : 0;
    return (
      <div className="wr-results-screen">
        <div className="wr-results-card">
          <Trophy className="wr-results-icon" />
          <h2>Session Complete!</h2>
          <p className="wr-results-sub">Synonyms Game</p>
          <div className="wr-results-stats">
            <div className="wr-stat">
              <Target className="wr-stat-icon" />
              <div className="wr-stat-value">{answered}</div>
              <div className="wr-stat-label">Answered</div>
            </div>
            <div className="wr-stat">
              <Check className="wr-stat-icon" style={{ color: "#16a34a" }} />
              <div className="wr-stat-value">{accuracy}%</div>
              <div className="wr-stat-label">Accuracy</div>
            </div>
            <div className="wr-stat">
              <Clock className="wr-stat-icon" />
              <div className="wr-stat-value">
                {formatTime(Date.now() - startTime)}
              </div>
              <div className="wr-stat-label">Time</div>
            </div>
          </div>
          <div className="wr-elo-summary">
            Final ELO: <strong>{userElo}</strong> &nbsp;·&nbsp; Coins earned:{" "}
            <strong>{coinsEarned}</strong>
          </div>
          <div className="wr-results-actions">
            <button
              className="wr-btn-primary"
              onClick={() => {
                shownThisSession.current.clear();
                setGameState("loading");
                setCorrect(0);
                setWrong(0);
                setAnswered(0);
                setStreak(0);
                setEloLog([]);
                generateQuestion(vocabulary, userElo);
              }}
            >
              <RefreshCw size={15} /> Play Again
            </button>
            <button
              className="wr-btn-secondary"
              onClick={goBack}
            >
              <ArrowLeft size={15} /> Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ── Main game ── */
  return (
    <div className="wr-game">
      {/* Header */}
      <div className="wr-header">
        <div className="wr-header-left">
          <h1>Synonyms</h1>
          <div className="wr-elo-badge">
            <Zap size={14} /> {userElo}
          </div>
        </div>
        <div className="wr-header-right">
          <div className="wr-score">
            <span className="wr-score-correct">
              <Check size={14} /> {correct}
            </span>
            <span className="wr-score-wrong">
              <X size={14} /> {wrong}
            </span>
          </div>
        </div>
      </div>

      {/* Question card */}
      <div className="wr-main">
        {question && (
          <div className="wr-card-wrapper">
            <div className="wr-year-bar">
              <label className="wr-year-label">Year group:</label>
              <select
                className="wr-year-select"
                value={selectedTier}
                onChange={(e) => {
                  shownThisSession.current.clear();
                  setSelectedTier(parseInt(e.target.value, 10));
                  setGameState("loading");
                }}
              >
                {DISPLAY_YEAR_OPTIONS.map((opt) => (
                  <option key={opt.tier} value={opt.tier}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="wr-card">
            <div className="wr-word-display">
              <p className="wr-word">{question.word}</p>
              <p className="wr-definition">{question.definition}</p>
              <span className="wr-tier-badge">{tierLabel(question.tier)}</span>
            </div>

            <div className="wr-question-row">
              <p className="wr-question-text">
                Which word is a synonym of "{question.word}"?
              </p>
              <button
                className={`wr-speak-btn ${speaking ? "speaking" : ""}`}
                onClick={() => speak(question)}
                title={speaking ? "Stop reading" : "Read question aloud"}
                aria-label={speaking ? "Stop reading" : "Read question aloud"}
                aria-pressed={speaking}
              >
                <Volume2 size={18} />
              </button>
            </div>

            <div className="wr-options">
              {question.options.map((opt, i) => {
                let cls = "";
                if (gameState === "feedback") {
                  if (opt === question.correctAnswer) cls = "correct";
                  else if (opt === selected && opt !== question.correctAnswer)
                    cls = "wrong";
                }
                return (
                  <button
                    key={opt}
                    ref={i === 0 ? firstOptionRef : null}
                    className={`wr-option ${cls}`}
                    onClick={() => handleSelect(opt)}
                    disabled={gameState === "feedback"}
                  >
                    <span className="wr-option-key">{KEYS[i]}</span>
                    {opt}
                  </button>
                );
              })}
            </div>

            <div className="wr-progress-wrap">
              <div className="wr-progress-bar">
                <div
                  className="wr-progress-fill"
                  style={{
                    width: `${Math.min(100, (correct / Math.max(1, answered)) * 100)}%`,
                  }}
                />
              </div>
              <span className="wr-progress-label">
                {correct}/{answered} correct
              </span>
            </div>

            <div className="wr-streak">
              {streak >= 3 ? `🔥 ${streak} streak!` : ""}
            </div>

            <div className="wr-card-actions wr-card-actions--grouped">
              <button className="wr-finish-btn" onClick={finishSession}>
                Complete Session
              </button>
            </div>
          </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SynonymsGame;
